import { APP_ROUTES } from "../constants";
import { PhoneNumberPage } from "../pages/PhoneNumberPage";
import { AddCardPage } from "../pages/AddCardPage";
import { AddCardAddressPage } from "../pages/AddCardAddressPage";
import { AddCardProcessingPage } from "../pages/AddCardProcessingPage";
import { AddCardCollectPage } from "../pages/AddCardCollectPage";
import { ErrorPage } from "../shared/error/pages/ErrorPage";
import { SessionExpiredPage } from "../shared/error/pages/SessionExpiredPage";
import { ServiceUnavailablePage } from "../shared/error/pages/ServiceUnavailablePage";
import { MaintenancePage } from "../shared/error/pages/MaintenancePage";
import { LandingPage } from "../pages/LandingPage";
import { LoadingPage } from "../pages/LoadingPage";
import { WelcomePage } from "../pages/WelcomePage";
import { TermDetailPage } from "../pages/TermDetailPage";
import { SmsTermsPage } from "../pages/SmsTermsPage";
import { ReceiptSummary } from "../pages/ReceiptSummaryPage";
import { SendingMoneyPage } from "../pages/SendingMoneyPage";
import { TransactionsPage } from "../pages/TransactionsPage";
import { TransferPageRouter } from "../pages/transfer";
import { CipPage } from "../pages/CipPage";
import { SupportPageV2 } from "../pages/SupporPageV2";
import { LegalPage } from "../pages/LegalPage";

export interface IAppRoutes {
  path: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: any;
}

export const routesList: IAppRoutes[] = [
  { path: APP_ROUTES.PHONE_PAGE, Component: PhoneNumberPage },
  { path: APP_ROUTES.LOADING_PAGE, Component: LoadingPage },
  // Magic-link entry: the flow is encoded in the linkId itself
  // (e.g. link_cashi_m2m_xxxx), so the URL no longer carries a flow segment.
  { path: "/:linkId", Component: LoadingPage },
  { path: "/default", Component: LoadingPage },
  { path: "/:linkId/default", Component: LoadingPage },
  { path: "/send", Component: LoadingPage },
  { path: "/:linkId/send", Component: LoadingPage },
  { path: APP_ROUTES.WELCOME_PAGE, Component: WelcomePage },
  { path: APP_ROUTES.LANDING_PAGE, Component: LandingPage },
  { path: APP_ROUTES.TERM_DETAIL_PAGE, Component: TermDetailPage },
  { path: APP_ROUTES.SMS_TERMS_PAGE, Component: SmsTermsPage },
  { path: APP_ROUTES.TRANSACTIONS_PAGE, Component: TransactionsPage },
  { path: APP_ROUTES.TRANSFER_PAGE, Component: TransferPageRouter },
  { path: APP_ROUTES.ADD_CARD_PAGE, Component: AddCardPage },
  { path: APP_ROUTES.ADD_CARD_ADDRESS_PAGE, Component: AddCardAddressPage },
  {
    path: APP_ROUTES.ADD_CARD_PROCESSING_PAGE,
    Component: AddCardProcessingPage,
  },
  { path: APP_ROUTES.ADD_CARD_COLLECT_PAGE, Component: AddCardCollectPage },
  { path: APP_ROUTES.SENDING_MONEY_PAGE, Component: SendingMoneyPage },
  { path: APP_ROUTES.CIP_PAGE, Component: CipPage },
  { path: APP_ROUTES.RECEIPT_SUMMARY_PAGE, Component: ReceiptSummary },
  { path: APP_ROUTES.ERROR_PAGE, Component: ErrorPage },
  { path: APP_ROUTES.SESSION_EXPIRED_PAGE, Component: SessionExpiredPage },
  {
    path: APP_ROUTES.SERVICE_UNAVAILABLE_PAGE,
    Component: ServiceUnavailablePage,
  },
  {
    path: APP_ROUTES.MAINTENANCE_PAGE,
    Component: MaintenancePage,
  },
  { path: APP_ROUTES.SUPPORT_PAGE, Component: SupportPageV2 },
  // { path: APP_ROUTES.SUPPORT_PAGE, Component: SupportPage },
  { path: APP_ROUTES.LEGAL_PAGE, Component: LegalPage },
];

export const formatMoney = (value: string) => {
  if (!value || value === ".00") return "";
  value = value.replace(/[^\d.]/g, "");
  const [intPart, decPart = ""] = value.split(".");
  const decimals = (decPart + "00").slice(0, 2);
  return `${intPart || "0"}.${decimals}`;
};

export type amountFormatterProps = (
  amount: number,
  currency?: string,
  numberFormatOptions?: {
    country?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
) => string;

export const amountFormatter: amountFormatterProps = (
  amount,
  currency = "USD",
  {
    country = navigator.language || "en-US",
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = {}
) =>
  new Intl.NumberFormat(country, {
    style: "decimal",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(amount);

/** Decimal amount + ISO code; USD also includes `$`. */
export function formatLabeledAmount(
  amount: number,
  currency = "USD",
  numberFormatOptions?: {
    country?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  const formatted = amountFormatter(amount, currency, numberFormatOptions);
  const code = currency.toUpperCase();
  return code === "USD" ? `$${formatted} ${code}` : `${formatted} ${code}`;
}

export const formatReceiptDate = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = String(date.getFullYear());
  return `${day}/${month}/${year}`;
};

export const getCurrentDateFormatted = (): string => {
  const date = new Date();

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
};

export const addBusinessDays = (date: Date): string => {
  const result = new Date(date);
  let added = 0;

  while (added < 5) {
    result.setDate(result.getDate() + 1);

    const day = result.getDay();
    // 0 = domingo, 6 = sábado
    if (day !== 0 && day !== 6) {
      added++;
    }
  }

  return formatReceiptDate(result);
};
